import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import { Button, Dialog, DialogActions, DialogContent, List, ListItem, ListItemText, Theme } from "@mui/material";
import { createStyles, makeStyles } from "@mui/styles";
import { useDraggable } from "shared-ui/hooks/useDraggable";
import { sortByKey } from "@notemeal/utils-sort";
import { ComponentType, useState } from "react";
import { animated } from "react-spring";
import DialogTitle from "../../componentLibrary/DialogTitle";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    listItem: {
      cursor: "pointer",
    },
  })
);

// Cast to ComponentType<any> before wrapping: react-spring's animated() combined with MUI's
// ListItem prop types (sx + polymorphic component prop) exceeds TypeScript's type-complexity
// budget (TS2590) otherwise.
const AnimatedListItem = animated(ListItem as ComponentType<any>);

export interface Reorderable {
  id: string;
  position: number;
  name: string;
}

interface ReorderDialogProps<T extends Reorderable> {
  open: boolean;
  onClose: () => void;
  onDone: (reorderableArray: readonly T[]) => void;
  reorderableArray: readonly T[];
  title?: string;
}

const ReorderDialog = <T extends Reorderable>({
  open,
  onClose,
  onDone,
  reorderableArray: _reorderableArray,
  title,
}: ReorderDialogProps<T>) => {
  const [reorderableArray, setReorderableArray] = useState(_reorderableArray);
  const sortedReorderableArray = sortByKey(reorderableArray, "position");

  const handleChangeOrder = (ids: string[]) => {
    const reorderedDiningStations = ids.flatMap((id, index) => {
      const matchingDiningStation = reorderableArray.find(item => item.id === id);
      return matchingDiningStation ? [{ ...matchingDiningStation, position: index + 1 }] : [];
    });
    setReorderableArray(reorderedDiningStations);
  };

  const handleDone = () => {
    onDone(reorderableArray);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={"sm"}
      fullWidth={true}>
      <DialogTitle title={title || "Drag dining stations to re-order"} onClose={onClose} />
      <ReorderDialogContent
        key={sortedReorderableArray.map(item => item.id).join(",")}
        sortedReorderableArray={sortedReorderableArray}
        onChangeOrder={handleChangeOrder}
      />
      <DialogActions>
        <Button variant="outlined" onClick={onClose}>
          Cancel
        </Button>
        <Button onClick={handleDone}>Done</Button>
      </DialogActions>
    </Dialog>
  );
};

interface ReorderDialogContentProps<T extends Reorderable> {
  sortedReorderableArray: readonly T[];
  onChangeOrder: (itemIds: string[]) => void;
}

const ReorderDialogContent = <T extends Reorderable>({ sortedReorderableArray, onChangeOrder }: ReorderDialogContentProps<T>) => {
  const classes = useStyles();

  const draggables = sortedReorderableArray.map(({ id }, index) => ({
    id,
    index,
    height: 48.1,
  }));
  const { springs, bind } = useDraggable(draggables, onChangeOrder);

  return (
    <DialogContent>
      <List>
        {sortedReorderableArray.map(({ id: itemId, name: itemName }) => {
          const springObject = draggables.find(({ id }) => id === itemId);
          if (!springObject) {
            return;
          }

          const spring = springs[springObject.index];
          const bindProps = bind(springObject.index);

          if (!spring || !bindProps) {
            return;
          }

          const { zIndex, y, shadow } = spring;

          return (
            <AnimatedListItem
              key={itemId}
              className={classes.listItem}
              {...bindProps}
              style={{
                zIndex,
                boxShadow: shadow.to(s => `rgba(0, 0, 0, 0.15) 0px 0px ${s}px 0px`),
                transform: y.to(y => `translate3d(0, ${y}px, 0)`),
              }}
            >
              <ListItemText primary={itemName} />
              <DragIndicatorIcon />
            </AnimatedListItem>
          );
        })}
      </List>
    </DialogContent>
  );
};

export default ReorderDialog;
